import { Component, OnInit, HostListener } from '@angular/core'
import { FeatureKeys } from '@app/features/feature-keys.generated'
import releases from '../release-logs.json'
import { AuthProvider } from './providers/auth.provider'

import { MenuController, ModalController } from '@ionic/angular'
import { FilterService, PrimeNGConfig } from 'primeng/api'
import { DeviceController } from './controllers/device.controller'
import { GlobalController } from './controllers/global.controller'

import { NavigationEnd, Router } from '@angular/router'
import { filter } from 'rxjs/operators'
import { getAppLogoSrc, MESSAGE_TYPES } from 'src/app/utils/constants.utils'
import { ChangelogComponent } from './components/changelog/changelog.component'
import { LogoutService } from './services/logout.service'
import { NotificationsService } from './services/notifications.service'
import { UnifiedSearchComponent } from './components/unified-search/unified-search.component'
import { registerSearchMatchMode } from './utils/search.utils'
import { getPrimeNGTranslation } from './utils/primeng-locale.utils'
import { TranslocoService } from '@jsverse/transloco'
import { Settings } from 'luxon'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  public appPages = []
  public dashboards = []
  showSideMenu = false
  userRole = 'employee'
  releases = []
  sideMenuEnabled = false
  logoSrc = getAppLogoSrc()

  constructor(
    private router: Router,
    public authProvider: AuthProvider,
    private deviceCtrl: DeviceController,
    public globalCtrl: GlobalController,
    public logoutService: LogoutService,
    private modalCtrl: ModalController,
    private PrimeNGConfig: PrimeNGConfig,
    public notificationsService: NotificationsService,
    private menuController: MenuController,
    private transloco: TranslocoService,
    filterService: FilterService
  ) {
    registerSearchMatchMode(filterService)
    this.globalCtrl.events.subscribe('user:login', () => {
      this.showSideMenu = true
      this.sideMenuEnabled = true
      this.userRole = this.authProvider.getUserRole()
      return false
    })
    this.globalCtrl.events.subscribe('user:logout', () => {
      this.showSideMenu = false
      this.sideMenuEnabled = false
    })
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (this.authProvider.getUserRole() === 'admin') {
        this.notificationsService.updateMessageAlerts()
        this.notificationsService.updateHourAlerts()
        this.notificationsService.updateAlerts(7)
      }
    })
  }

  ngOnInit() {
    this.releases = this.makeReleases()

    this.appPages = [
      {
        titleKey: 'MENU.OVERVIEW',
        url: '/overview',
        icon: 'podium',
        data_cy: 'overviewPage',
        feature: FeatureKeys.SIDEBAR_OVERVIEW,
      },
      {
        titleKey: 'MENU.MATCHING',
        url: '/matching',
        icon: 'link',
        data_cy: 'matchingPage',
        feature: FeatureKeys.SIDEBAR_MATCHING,
      },
      {
        titleKey: 'MENU.ORDERS',
        url: '/orders',
        icon: 'file-tray-full',
        data_cy: 'orderPage',
        feature: FeatureKeys.SIDEBAR_ORDERS,
      },
      {
        titleKey: 'MENU.CLIENTS',
        url: '/clients',
        icon: 'happy',
        data_cy: 'clientsPage',
        feature: FeatureKeys.SIDEBAR_CLIENTS,
      },
      {
        titleKey: 'MENU.SUPERVISORS',
        url: '/supervisor',
        icon: 'megaphone',
        data_cy: 'supervisorPage',
        feature: FeatureKeys.SIDEBAR_SUPERVISOR,
      },
      {
        titleKey: 'MENU.EMPLOYEES',
        url: '/employees',
        icon: 'people',
        data_cy: 'employeesPage',
        feature: FeatureKeys.SIDEBAR_EMPLOYEES,
      },
      {
        titleKey: 'MENU.APPLICANTS',
        url: '/applicants',
        icon: 'people-circle',
        data_cy: 'applicantsPage',
        feature: FeatureKeys.SIDEBAR_APPLICANTS,
      },
      {
        feature: FeatureKeys.SIDEBAR_COMMUNICATIONS,
        parent: {
          titleKey: 'MENU.COMMUNICATIONS',
          url: '/messages',
          icon: 'mail',
          data_cy: 'messagesPage',
          alerts: true,
        },
        children: [
          {
            titleKey: 'MENU.MESSAGES_GENERAL',
            url: '/messages/general',
            alerts: true,
            messageType: MESSAGE_TYPES.GENERAL,
          },
          {
            titleKey: 'MENU.MESSAGES_ASSIGNATION',
            url: '/messages/assignation',
            alerts: true,
            messageType: MESSAGE_TYPES.ASSIGNATION,
          },
          {
            titleKey: 'MENU.MESSAGES_APPLICANTS',
            url: '/messages/applicants',
            alerts: true,
            messageType: MESSAGE_TYPES.APPLICANTS,
          },
        ],
      },
      {
        titleKey: 'MENU.TIMESHEETS',
        url: '/timesheets',
        icon: 'time',
        data_cy: 'timesheetPage',
        feature: FeatureKeys.SIDEBAR_TIMESHEETS,
      },
      {
        titleKey: 'MENU.TOOLS',
        url: '/tools',
        icon: 'hammer',
        data_cy: 'ToolsPage',
        feature: FeatureKeys.SIDEBAR_TOOLS,
      },
      {
        titleKey: 'MENU.SKILLS',
        url: '/skills',
        icon: 'school',
        data_cy: 'skillsPage',
        feature: FeatureKeys.SIDEBAR_SKILLS,
      },
      {
        titleKey: 'MENU.TEMPLATE_DOCS',
        url: '/template-docs',
        icon: 'document-text',
        data_cy: 'templateDocsPage',
        feature: FeatureKeys.SIDEBAR_TEMPLATE_DOCS,
      },
      {
        titleKey: 'MENU.FORMATIONS',
        url: '/formations',
        icon: 'easel',
        data_cy: 'formationsPage',
        feature: FeatureKeys.SIDEBAR_FORMATIONS,
      },
      {
        titleKey: 'MENU.GLOBAL_SEARCH',
        onClick: () => this.openUnifiedSearchModal(),
        icon: 'search',
        data_cy: 'globalSearchesPage',
        feature: FeatureKeys.SIDEBAR_GLOBAL_SEARCH,
      },
    ]

    this.dashboards = [
      {
        titleKey: 'MENU.DASHBOARD_EMPLOYEES',
        url: '/dashboard-employees',
        icon: 'people',
        feature: FeatureKeys.SIDEBAR_DASHBOARD_EMPLOYEES,
      },
      {
        titleKey: 'MENU.DASHBOARD_SUPERVISORS',
        url: '/dashboard-no-orders',
        icon: 'megaphone',
        feature: FeatureKeys.SIDEBAR_DASHBOARD_SUPERVISORS,
      },
      {
        titleKey: 'MENU.DASHBOARD_EMPLOYEE_CALLS',
        url: '/dashboard-employee-call',
        icon: 'call',
        feature: FeatureKeys.SIDEBAR_DASHBOARD_EMPLOYEE_CALLS,
      },
      {
        titleKey: 'MENU.DASHBOARD_CLIENTS',
        url: '/clients-dashboard',
        icon: 'happy',
        feature: FeatureKeys.SIDEBAR_DASHBOARD_CLIENTS,
      },
      {
        titleKey: 'MENU.DASHBOARD_MAP',
        url: '/dashboard/map',
        icon: 'map',
        feature: FeatureKeys.SIDEBAR_DASHBOARD_MAP,
      },
      {
        titleKey: 'MENU.ABSENCES',
        url: '/absences',
        icon: 'calendar',
        feature: FeatureKeys.SIDEBAR_ABSENCES,
      },
      {
        titleKey: 'MENU.ALERTS',
        url: '/alerts',
        icon: 'alert-circle',
        alerts: true,
        messageType: MESSAGE_TYPES.SITES_ALERTS,
        feature: FeatureKeys.SIDEBAR_ALERTS,
      },
      {
        titleKey: 'MENU.HOUR_ALERTS',
        url: '/hour-alerts',
        icon: 'time',
        alerts: true,
        messageType: MESSAGE_TYPES.HOUR_ALERTS,
        feature: FeatureKeys.SIDEBAR_HOUR_ALERTS,
      },
    ]

    // no need to configure luxon day of the week start, it is already set to monday

    // Les libelles PrimeNG (calendrier, filtres de table) et la locale par
    // defaut de Luxon suivent la langue active : langChanges$ emet la langue
    // courante a l'abonnement, puis a chaque bascule. Luxon remplace les
    // `Settings.defaultLocale = 'fr'` poses autrefois au chargement de pages.
    this.transloco.langChanges$.subscribe(lang => {
      Settings.defaultLocale = lang
      this.PrimeNGConfig.setTranslation(getPrimeNGTranslation(lang))
    })

    this.showSideMenu = true
  }

  makeReleases() {
    return releases.reduceRight((acc, release, index, arr) => {
      if (!release.body.length) {
        // Find the next non-empty body in the list, as many releases can be empty in a row
        const nextFilledRelease = arr.slice(index + 1).find(r => r.body.length)

        if (nextFilledRelease) {
          // Assign the body of the next non-empty release
          release.body = nextFilledRelease.body

          // Remove the previous element in the list (next non-empty or not)
          acc.shift()
        }
      }

      acc.unshift(release)
      return acc
    }, [])
  }

  showSplitPane() {
    if (this.authProvider.getUserRole() !== 'admin') {
      this.sideMenuEnabled = false
      return false
    }
    this.sideMenuEnabled = true

    return this.deviceCtrl.desktopView && this.showSideMenu
  }

  async openChangesModal() {
    const showReleaseModal = await this.modalCtrl.create({
      component: ChangelogComponent,
      cssClass: 'desktopReleaseLogsModal',
      componentProps: {
        releases: this.releases,
      },
    })

    await showReleaseModal.present()
  }

  accordionClicked(event: Event) {
    event.stopPropagation()
  }

  async childItemClicked(event: Event) {
    await this.menuController.close()
  }

  /**
   * Opens the unified search modal
   */
  async openUnifiedSearchModal() {
    const modal = await this.modalCtrl.create({
      component: UnifiedSearchComponent,
      cssClass: 'desktopBiggerModal',
    })
    await modal.present()
  }

  /**
   * Listen for Ctrl+K keyboard shortcut
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault()
      this.openUnifiedSearchModal()
    }
  }
}
